import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { NgxSpinnerService } from "ngx-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { ChitPlan, ChitService } from "../../shared/services/chit.service";
import { ToastrService } from "ngx-toastr";
import { AuthService } from "../../shared/services/auth";
import { RouterLink } from "@angular/router";
import { PdfGeneratorService } from "../../shared/services/pdf-generator.service";
import { PriceService } from "../../shared/services/price.service";

@Component({
  selector: "app-add-customer.dialog",
  standalone: true,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTableModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatRadioModule,
    RouterLink,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./add-customer.dialog.component.html",
  styleUrl: "./add-customer.dialog.component.scss",
})
export class AddCustomerDialogComponent implements OnInit {
  customerForm!: FormGroup;

  chitPlans: ChitPlan[] = [];
  minAmount: number = 0;
  maxAmount: number = 0;
  benefitPoints: string[] = [];
  descriptionPoints: string[] = [];
  selectedPlan: ChitPlan | null = null;

  isGoldPlanDueRange = ["Monthly"];
  isNonGoldPlanDueRange = ["Daily", "Weekly", "Monthly"];

  dueRangeOptions: string[] = [];
  currentGoldRate: number | null = null;

  constructor(
    private fb: FormBuilder,
    // private dialogRef: MatDialogRef<AddCustomerDialogComponent>,
    private spinnerSvc: NgxSpinnerService,
    private chitSvc: ChitService,
    private notification: ToastrService,
    private authService: AuthService,
    private PdfService: PdfGeneratorService,
    private priceSvc: PriceService,
    private notificationSvc: ToastrService
  ) {}

  ngOnInit(): void {
    this.customerForm = this.fb.group({
      customerName: ["", Validators.required],
      email: ["", [Validators.email]],
      phoneNumber: [
        "",
        [
          Validators.required,
          Validators.pattern(/^[0-9]{10}$/), // exactly 10 digits
        ],
      ],
      doorNo: ["", Validators.required],
      area: ["", Validators.required],
      pinCode: ["", [Validators.pattern(/^[0-9]{6}$/), Validators.required] ], // optional: Indian PIN validation
      city: ["", Validators.required],
      state: ["", Validators.required],
      dob: ["", Validators.required],
      gender: ["", Validators.required],
      plan: ["", Validators.required],
      dueType: ["", Validators.required],
      amount: [
        "",
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/), // only digits
        ],
      ],
      pan: [
        "",
        [
          Validators.pattern(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/), // PAN format: ABCDE1234F
        ],
      ],
      aadhaar: [
        "",
        [
          Validators.pattern(/^[0-9]{12}$/), // 12-digit Aadhaar
        ],
      ],
    });

    // Watch for plan changes
    this.customerForm.get("plan")?.valueChanges.subscribe((planId) => {
      const selectedPlan = this.chitPlans.find((p) => p.id === planId);
      const amountControl = this.customerForm.get("amount");

      if (selectedPlan && amountControl) {
        const min = parseFloat(selectedPlan.minAmount);
        const max = parseFloat(selectedPlan.maxAmount);
        this.minAmount = min;
        this.maxAmount = max;

        amountControl.setValidators([
          Validators.required,
          this.amountIncrementValidator(min, max),
        ]);
        amountControl.updateValueAndValidity();
      }
    });

    this.getChitPlans();
    this.getCurrentGoldRate();
  }

  getCurrentGoldRate() {
    // Show spinner before starting API call
    this.spinnerSvc.show();

    this.priceSvc.getGoldPrices().subscribe({
      next: (res: any) => {
        if (res?.pricePerGram) {
          this.currentGoldRate = res.pricePerGram;
        } else {
          this.currentGoldRate = null;
        }
      },
      error: (err) => {

        // Hide spinner on error
        this.spinnerSvc.hide();

        this.currentGoldRate = null;

        // Optional: show notification/snackbar/toast
        this.notificationSvc.error(
          "Failed to fetch today’s gold rate. Please try again later."
        );
      },
      complete: () => {
        // Always hide spinner once done
        this.spinnerSvc.hide();
        this.getChitPlans();
      },
    });
  }

  amountIncrementValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl) => {
      const value = Number(control.value);
      if (isNaN(value)) return { invalidNumber: true };

      if (value < min || value > max) return { outOfRange: true };

      // Check increments
      if (value <= 500 && value % 100 !== 0) return { invalidIncrement: true };
      if (value > 500 && value % 1000 !== 0) return { invalidIncrement: true };

      return null;
    };
  }

  getChitPlans(): void {
    this.spinnerSvc.show();
    this.chitSvc.getAllChits().subscribe({
      next: (response) => {
        if (response.success) {
          this.chitPlans = response.data;
        } else {
          this.notification.error("Failed to load chit plans", "Error");
        }
        this.spinnerSvc.hide();
      },
      error: (err) => {
        this.notification.error(err.message, "Error");
        this.spinnerSvc.hide();
      },
    });
  }

  onPlanChange(planId: number) {
    this.selectedPlan =
      this.chitPlans.find((plan) => plan.id === planId) || null;

    if (this.selectedPlan) {
      this.benefitPoints = this.selectedPlan.benefit
        .split(".")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      this.descriptionPoints = this.selectedPlan.description
        .split(".")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      this.dueRangeOptions = this.selectedPlan.isGoldPlan
        ? this.isGoldPlanDueRange
        : this.isNonGoldPlanDueRange;
    } else {
      this.benefitPoints = [];
      this.descriptionPoints = [];
    }
  }

  createCustomer() {
  if (!this.customerForm.valid) {
    this.notification.error(
      "Please fill all required fields correctly.",
      "Validation Error"
    );
    return;
  }

  const payload = this.customerForm.getRawValue();

  payload.amount = parseInt(payload.amount);
  payload.email = payload.email || null;
  payload.dueType = payload.dueType?.toUpperCase();
  payload.pinCode = payload.pinCode?.toString();
  payload.phoneNumber = payload.phoneNumber?.toString();

  if (payload.dueType === "MONTHLY" && payload.amount < 500) {
    this.notification.error("Amount must be at least 500 for Monthly Plan");
    return;
  }

  this.spinnerSvc.show();

  this.authService.createCustomerAccount(payload).subscribe({
    next: (res) => {
      if (!res?.data?.id) {
        this.spinnerSvc.hide();
        this.notification.error("Customer ID missing in response");
        return;
      }

      this.notification.success("Customer created successfully!", "Success");

      // Prepare payload for payChit
      const paymentPayload = {
        accountId: res.data.id,
        chitPlanId: payload.plan,
        currentGoldRate: this.currentGoldRate,
        dateOfPayment: new Date(),
        note: "Monthly payment - offline",
      };

      // Call payChit API
      this.chitSvc.payChit(paymentPayload).subscribe({
        next: (paymentRes) => {
          this.spinnerSvc.hide();

          if (!paymentRes?.data?.id) {
            this.notification.warning("Payment completed but no ID returned");
          } else {
            this.notification.success("Payment recorded successfully!", "Success");
          }

          // Reset form after both API calls succeed
          this.customerForm.reset();
          this.benefitPoints = [];
          this.descriptionPoints = [];
        },
        error: (err) => {
          this.spinnerSvc.hide();
          this.notification.error(
            "Failed to record payment",
            err?.message || "Server error"
          );
        },
      });
    },
    error: (err) => {
      this.spinnerSvc.hide();
      this.notification.error(
        "Customer creation failed",
        err?.message || "Server error"
      );
    },
  });
}


  createCustomerAndPrintReceipt() {
  if (!this.customerForm.valid) {
    this.notification.error(
      "Please fill all required fields correctly.",
      "Validation Error"
    );
    return;
  }

  const payload = this.customerForm.getRawValue();
  payload.amount = parseInt(payload.amount);
  payload.email = payload.email || null;
  payload.dueType = payload.dueType?.toUpperCase();
  payload.pinCode = payload.pinCode?.toString();
  payload.phoneNumber = payload.phoneNumber?.toString();

  if (payload.dueType === "MONTHLY" && payload.amount < 500) {
    this.notification.error("Amount must be at least 500 for Monthly Plan");
    return;
  }

  this.spinnerSvc.show();

  this.authService.createCustomerAccount(payload).subscribe({
    next: (res) => {
      if (!res?.data?.id) {
        throw new Error("Customer ID not received from server");
      }

      this.notification.success("Customer created successfully!", "Success");

      const paymentPayload = {
        accountId: res.data.id,
        chitPlanId: payload.plan,
        currentGoldRate: this.currentGoldRate,
        dateOfPayment: new Date(),
        note: "Monthly payment - offline",
      };

      this.chitSvc.payChit(paymentPayload).subscribe({
        next: (paymentRes) => {
          if (!paymentRes?.data?.id) {
            throw new Error("Payment ID not received from server");
          }

          this.chitSvc
            .getPrintDetails(res.data.id, payload.plan)
            .subscribe({
              next: (printData) => {
                if (!printData || !Array.isArray(printData) || !printData.length) {
                  this.notification.warning(
                    "No receipt data available to print"
                  );
                  return;
                }

                const customerName = payload.customerName;
                this.PdfService.generateReceiptPDF(printData, true, customerName);

                // Reset form & UI
                this.customerForm.reset();
                this.benefitPoints = [];
                this.descriptionPoints = [];
              },
              error: (err) => {
                this.notification.error(
                  "Failed to fetch print details",
                  err?.message || "Server error"
                );
              },
              complete: () => this.spinnerSvc.hide(),
            });
        },
        error: (err) => {
          this.spinnerSvc.hide();
          this.notification.error(
            "Failed to record payment",
            err?.message || "Server error"
          );
        },
      });
    },
    error: (err) => {
      this.spinnerSvc.hide();
      this.notification.error(
        "Customer creation failed",
        err?.message || "Server error"
      );
    },
    complete: () => this.spinnerSvc.hide(),
  });
}


  // close() {
  //   this.dialogRef.close();
  // }
}
